import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { FaSquareFull } from "react-icons/fa";
import axios from "axios";
import {
  Content,
  Section,
  Button2,
  StyledSvg,
  ContainerCardProduct,
  TextPadrao,
} from "./styles";
import { useSearchParams } from "react-router-dom";
import configs from "config";
import Filter from "components/Filter";
import Loader2 from "components/Loader2";
import ProductItem from "components/ProductItem";
import { hasSalePriceDiscount, shouldShowSaleDiscount } from "utils/app";

const ITEMS_PER_PAGE = 24;

const initialFiltersState = {
  categoria: [],
  tamanho: [],
  cor: [],
  ocasiao: [],
  marca: [],
  genero: [],
  preco: { min: null, max: null },
  orderBy: "", // string em vez de array facilita
};

const uniqueProductsById = (products) => {
  const seenIds = new Set();

  return products.filter((product) => {
    const productId = product?.id;

    if (productId == null) {
      return true;
    }

    if (seenIds.has(productId)) {
      return false;
    }

    seenIds.add(productId);
    return true;
  });
};

const CardProduct = ({ filters }) => {
  const [searchParams] = useSearchParams();

  // ----- UI / estado geral -----
  const [itemSize, setItemSize] = useState("large");
  const [selectedSize, setSelectedSize] = useState("large");
  const [hoveredProductId, setHoveredProductId] = useState(null);

  const [allProducts, setAllProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);

  const [isLoading, setIsLoading] = useState(false);       // loading geral
  const [apiCalled, setApiCalled] = useState(false);       // já buscou pelo menos uma vez?
  const [search, setSearch] = useState("");                // valor do search (para passar pro Filter)

  const [, setTypeFilter] = useState();          // usado pra search=novidades/sale/shop etc
  const [filtros, setFiltros] = useState(initialFiltersState);

  const [imageCache] = useState({});

  // ----- Derivados de URL -----
  const urlSearchValue = useMemo(
    () => searchParams.get("search") || "",
    [searchParams]
  );
  const busca = useMemo(
    () => searchParams.get("busca"),
    [searchParams]
  );
  const brandlist = useMemo(
    () => searchParams.get("brandlist"),
    [searchParams]
  );
  const categorylist = useMemo(
    () => searchParams.get("categorylist"),
    [searchParams]
  );

  // ----- Definição de mobile / grid size -----
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") return;

      const screenWidth = window.innerWidth;
      const mobile = screenWidth < 768;
      setIsMobile(mobile);

      if (!mobile) {
        const newSize = screenWidth < 1068 ? "large" : "normal";
        setItemSize(newSize);
        setSelectedSize(newSize);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ----- Helpers de normalização -----
  function removeAccentsAndModify(text) {
    if (!text) return text;
    let modifiedText = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    modifiedText = modifiedText.replace(/'/g, "");

    const upper = modifiedText.toUpperCase();
    if (upper === "ALA%C3%8FA" || upper === "ALAIA") modifiedText = "ALAIA";
    else if (upper === "SEE BY CHLOE") modifiedText = "SEE BY CHLOÉ";
    else if (upper === "CHLOE") modifiedText = "CHLOÉ";
    else if (upper === "REINALDO LOURENCO")
      modifiedText = "REINALDO LOURÊNÇO";
    else if (upper === "HERMES") modifiedText = "HERMÈS";

    return modifiedText;
  }

  function removeAccentsAndSpace(text) {
    if (!text) return text;
    let modifiedText = text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "");

    if (modifiedText === "CALCA") {
      return "CALÇA";
    }

    return modifiedText;
  }

  const preloadImages = useCallback(
    (imagePaths) => {
      imagePaths.forEach((path) => {
        if (!path) return;
        if (!imageCache[path]) {
          const img = new Image();
          img.src = path;
          imageCache[path] = img;
        }
      });
    },
    [imageCache]
  );

  const handleScrollToTop = () => {
    if (typeof window === "undefined") return;
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const formatPrice = (price) => {
    if (price == null) return "";
    return Number(price).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSizeButtonClick = (size) => {
    setItemSize(size);
    setSelectedSize(size);
  };

  // ----- Filtros vindos do componente Filter -----
  const handleFilterChange = useCallback((filtersProduct) => {
    // Aqui só atualiza estado; quem faz a requisição é o efeito de loadProducts
    setPage(1);
    setAllProducts([]);
    setHasMoreProducts(true);

    setFiltros((prev) => ({
      ...prev,
      ...filtersProduct,
      // garante que preco tenha sempre min/max mesmo que venham undefined
      preco: {
        min:
          filtersProduct.preco && filtersProduct.preco.min != null
            ? filtersProduct.preco.min
            : null,
        max:
          filtersProduct.preco && filtersProduct.preco.max != null
            ? filtersProduct.preco.max
            : null,
      },
      orderBy: filtersProduct.orderBy || "",
    }));
  }, []);

  // ----- Sincroniza filtro básico (props.filters) + URL search -----
  useEffect(() => {
    setSearch(urlSearchValue || "");
    setTypeFilter(filters);
  }, [urlSearchValue, filters]);

  // Sempre que o search da URL mudar, resetamos lista / página
  useEffect(() => {
    setPage(1);
    setAllProducts([]);
    setHasMoreProducts(true);
  }, [urlSearchValue, filters]);

  // ----- Função central de carregamento -----
  const loadProducts = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = `${configs.api}get-allproducts?limit=${ITEMS_PER_PAGE}&page=${page}&takeme=true`;

      // 1) filtro de tipo vindo do componente pai (novidades, sale, shop, etc.)
      if (filters != null && filters !== "") {
        url += `&search=${filters}`;
      }

      // 2) tratamento de brandlist / categorylist / search da URL
      const isBrandList =
        brandlist === "true" || brandlist === true || brandlist === "1";
      const isCategoryList =
        categorylist === "true" ||
        categorylist === true ||
        categorylist === "1";

      if (isBrandList && urlSearchValue) {
        if (urlSearchValue === "Alaïa") {
          url += `&search=ALAIA`;
        } else {
          url += `&brand[]=${removeAccentsAndModify(
            encodeURIComponent(urlSearchValue)
          )}`;


        }
      } else if (isCategoryList && urlSearchValue) {
        url += `&group[]=${removeAccentsAndModify(urlSearchValue)}`;
      } else if (urlSearchValue && urlSearchValue !== "shop") {
        if (urlSearchValue === "Alaïa") {
          url += `&search=ALAIA`;
        } else {
          url += `&search=${urlSearchValue}`;
        }
      }


      // 3) filtros vindos do estado `filtros` (Filter)
      const filtroTemItens = (filtro) =>
        Array.isArray(filtro) && filtro.length > 0;

      if (busca !== "true" && busca !== true) {
        // Categoria
        if (filtroTemItens(filtros.categoria)) {
          const categoriasSemAcentos = filtros.categoria.map(
            removeAccentsAndSpace
          );
          url += `&group[]=${categoriasSemAcentos
            .map(encodeURIComponent)
            .join("&group[]=")}`;
        }

        // Tamanho
        if (filtroTemItens(filtros.tamanho)) {
          url += `&size[]=${filtros.tamanho
            .map(encodeURIComponent)
            .join("&size[]=")}`;
        }

        if (filtroTemItens(filtros.genero)) {
          url += `&genre[]=${filtros.genero
            .map(encodeURIComponent)
            .join("&genre[]=")}`;
        }

        // Cor
        if (filtroTemItens(filtros.cor)) {
          url += `&color[]=${filtros.cor
            .map(encodeURIComponent)
            .join("&color[]=")}`;
        }

        // Ocasião
        if (filtroTemItens(filtros.ocasiao)) {
          url += `&ocasiao[]=${filtros.ocasiao
            .map(encodeURIComponent)
            .join("&ocasiao[]=")}`;
        }

        // Marca
        if (filtroTemItens(filtros.marca)) {
          const marcasEncodadas = filtros.marca.map(m =>
            encodeURIComponent(
              removeAccentsAndModify(m)
            )
          );

          url += `&brand[]=${marcasEncodadas.join("&brand[]=")}`;
        }

        // Preço
        if (
          filtros.preco &&
          filtros.preco.min !== null &&
          filtros.preco.max !== null
        ) {
          const precoString = JSON.stringify(filtros.preco);
          const precoParam = encodeURIComponent(precoString);
          url += `&preco=${precoParam}`;
        }

        // Ordenação (sortDirection)
        if (filtros.orderBy) {
          url += `&sortDirection=${filtros.orderBy}`;
        }
      }

      // ----- Chamada na API -----
      const response = await axios.get(url);
      const newProducts = uniqueProductsById(response.data.data || []);
      const total = response.data.total || 0;

      // Preload das imagens secundárias
      preloadImages(newProducts.map((p) => p.images?.[1]?.path));

      setAllProducts((prev) => {
        if (page === 1) {
          // primeira página sempre substitui
          return newProducts;
        }

        // nas próximas páginas, evita duplicados
        return uniqueProductsById([...prev, ...newProducts]);
      });

      setTotalProducts(total);
      setApiCalled(true);

      // regra de limite pra "novidades" continua
      if (filters === "novidades") {
        setHasMoreProducts((prevProductsLength) => {
          const totalAteAgora = allProducts.length + newProducts.length;
          return totalAteAgora < 100;
        });
      } else {
        setHasMoreProducts((prevProductsLength) => {
          const totalAteAgora = allProducts.length + newProducts.length;
          return totalAteAgora < total;
        });
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    filters,
    filtros,
    urlSearchValue,
    brandlist,
    categorylist,
    busca,
    preloadImages,
    allProducts.length, // apenas o length, pra cálculo de hasMore
  ]);

  // Chama a API sempre que page / filtros / search mudarem
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // ----- Lógica de produtos visíveis (ex.: sale) -----
  const visibleProducts = useMemo(() => {
    if (filters === "sale") {
      return allProducts.filter(hasSalePriceDiscount);
    }
    return allProducts;
  }, [allProducts, filters]);

  useEffect(() => {
    if (filters === "sale") {
      setHasMoreProducts(allProducts.length < totalProducts);
    } else {
      setHasMoreProducts(allProducts.length < totalProducts);
    }
  }, [filters, allProducts, totalProducts]);

  // ----- Hover de imagem -----
  const handleMouseEnter = (productId) => {
    setHoveredProductId(productId);
    const product = allProducts.find((p) => p.id === productId);
    if (product && product.images && product.images.length > 1) {
      const img = new Image();
      img.src = product.images[1].path;
    }
  };

  const handleMouseLeave = () => {
    setHoveredProductId(null);
  };

  // ----- Botão "Carregar mais" -----
  const handleLoadMore = () => {
    if (!hasMoreProducts || isLoading) return;
    setPage((prev) => prev + 1);
  };

  // Check de chaves duplicadas (debug)
  const hasDuplicateKeys =
    new Set(allProducts.map((product) => product.id)).size !==
    allProducts.length;

  if (hasDuplicateKeys) {
    console.error("Existem chaves duplicadas na lista de produtos!");
  }

  const isLoadingMore = isLoading && page > 1 && allProducts.length > 0;

  // ----- Render -----
  return (
    <ContainerCardProduct>
      <Filter onFilterChange={handleFilterChange} searchFilter={search} />

      {/* Botões de tamanho do card */}
      {!isMobile ? (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <StyledSvg
            onClick={() => handleSizeButtonClick("large")}
            viewBox="0 0 26 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            selected={selectedSize === "large"}
          >
            <g clipPath="url(#clip0_20_1638)">
              <path
                d="M1 0H11.9375V10.9375H1V0ZM15.0625 0V10.9375H26V0H15.0625ZM1 25H11.9375V14.0625H1V25ZM15.0625 25H26V14.0625H15.0625V25Z"
                fill="black"
              />
            </g>
            <defs>
              <clipPath id="clip0_20_1638">
                <rect
                  width="25"
                  height="25"
                  fill="white"
                  transform="translate(0.538086)"
                />
              </clipPath>
            </defs>
          </StyledSvg>

          <StyledSvg
            onClick={() => handleSizeButtonClick("normal")}
            viewBox="0 0 26 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            selected={selectedSize === "normal"}
          >
            <g clipPath="url(#clip0_20_1637)">
              <path
                d="M0.538086 0H7.89103V7.35294H0.538086V0ZM9.36162 7.35294H16.7146V0H9.36162V7.35294ZM18.1851 0V7.35294H25.5381V0H18.1851ZM0.538086 16.1765H7.89103V8.82353H0.538086V16.1765ZM9.36162 16.1765H16.7146V8.82353H9.36162V16.1765ZM18.1851 16.1765H25.5381V8.82353H18.1851V16.1765ZM0.538086 25H7.89103V17.6471H0.538086V25ZM9.36162 25H16.7146V17.6471H9.36162V25ZM18.1851 25H25.5381V17.6471H18.1851V25Z"
                fill="black"
              />
            </g>
            <defs>
              <clipPath id="clip0_20_1637">
                <rect
                  width="25"
                  height="25"
                  fill="white"
                  transform="translate(0.538086)"
                />
              </clipPath>
            </defs>
          </StyledSvg>

          <StyledSvg
            onClick={() => handleSizeButtonClick("small")}
            viewBox="0 0 26 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            selected={selectedSize === "small"}
          >
            <g clipPath="url(#clip0_20_1636)">
              <path
                d="M1.00024 0H6.00024V5H1.00024V0ZM7.66691 5H12.6669V0H7.66691V5ZM14.3336 5H19.3336V0H14.3336V5ZM21.0002 0V5H26.0002V0H21.0002ZM1.00024 11.6667H6.00024V6.66667H1.00024V11.6667ZM7.66691 11.6667H12.6669V6.66667H7.66691V11.6667ZM14.3336 11.6667H19.3336V6.66667H14.3336V11.6667ZM21.0002 11.6667H26.0002V6.66667H21.0002V11.6667ZM1.00024 18.3333H6.00024V13.3333H1.00024V18.3333ZM7.66691 18.3333H12.6669V13.3333H7.66691V18.3333ZM14.3336 18.3333H19.3336V13.3333H14.3336V18.3333ZM21.0002 18.3333H26.0002V13.3333H21.0002V18.3333ZM1.00024 25H6.00024V20H1.00024V25ZM7.66691 25H12.6669V20H7.66691V25ZM14.3336 25H19.3336V20H14.3336V25ZM21.0002 25H26.0002V20H21.0002V25Z"
                fill="black"
              />
            </g>
            <defs>
              <clipPath id="clip0_20_1636">
                <rect
                  width="25"
                  height="25"
                  fill="white"
                  transform="translate(0.538086)"
                />
              </clipPath>
            </defs>
          </StyledSvg>
        </div>
      ) : (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <StyledSvg
            onClick={() => handleSizeButtonClick("one")}
            selected={selectedSize === "one"}
          >
            <FaSquareFull
              style={{ fontSize: "25px", marginLeft: "10px" }}
            />
          </StyledSvg>
          <StyledSvg
            onClick={() => handleSizeButtonClick("large")}
            viewBox="0 0 26 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            selected={selectedSize === "large"}
          >
            <g clipPath="url(#clip0_20_1638)">
              <path
                d="M1 0H11.9375V10.9375H1V0ZM15.0625 0V10.9375H26V0H15.0625ZM1 25H11.9375V14.0625H1V25ZM15.0625 25H26V14.0625H15.0625V25Z"
                fill="black"
              />
            </g>
            <defs>
              <clipPath id="clip0_20_1638">
                <rect
                  width="25"
                  height="25"
                  fill="white"
                  transform="translate(0.538086)"
                />
              </clipPath>
            </defs>
          </StyledSvg>
        </div>
      )}

      {/* Lista de produtos */}
      <Section>
        <Content>
          {/* Loader de primeira carga */}
          {isLoading && page === 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Loader2 />
            </div>
          )}

          {apiCalled && visibleProducts.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              {!isLoading && (
                <TextPadrao>Nenhum produto encontrado.</TextPadrao>
              )}
            </div>
          ) : (
            visibleProducts.map((product) => {
              const mainPhotoIndex = product.images?.findIndex(
                (image) => image.foto_principal
              );

              return (
                <ProductItem
                  key={product.id}
                  product={product}
                  itemSize={itemSize}
                  hoveredProductId={hoveredProductId}
                  mainPhotoIndex={mainPhotoIndex}
                  handleMouseEnter={handleMouseEnter}
                  handleMouseLeave={handleMouseLeave}
                  formatPrice={formatPrice}
                  desconto={shouldShowSaleDiscount(product)}
                />
              );
            })
          )}

          {/* Loader de "Carregar mais" */}
          {isLoadingMore && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                marginTop: "16px",
              }}
            >
              <Loader2 />
            </div>
          )}

          {/* Botão de paginação */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              marginTop: "3%",
            }}
          >
            {hasMoreProducts ? (
              <Button2 onClick={handleLoadMore} disabled={isLoadingMore}>
                {isLoadingMore ? "Carregando..." : "Carregar mais..."}
              </Button2>
            ) : (
              !isLoading && (
                <Button2 onClick={handleScrollToTop}>
                  ir para o topo...
                </Button2>
              )
            )}
          </div>
        </Content>
      </Section>
    </ContainerCardProduct>
  );
};

export default CardProduct;
